import { ref, watch, onScopeDispose, type Ref } from 'vue';

export type QuoteResult = {
  available: boolean;
  reason?: 'capacity' | 'dates_booked' | 'invalid_dates' | 'no_pricing';
  nights: number;
  tier: 'daily' | 'weekly';
  nightlyBreakdown: { date: string; seasonName: string; rate: number }[];
  subtotalUsd: number;
  cleaningUsd: number;
  prepaymentUsd: number;
  extraGuestUsd: number;
  totalUsd: number;
  poolIncluded: boolean;
};

export type QuoteInput = {
  listingId: Ref<number | null>;
  checkin: Ref<string>;
  checkout: Ref<string>;
  guests: Ref<number>;
};

export type QuoteOptions = {
  debounceMs?: number;
  // Override the endpoint — e.g. the admin booking form uses /api/admin/quote,
  // which prices any dates and ignores availability.
  endpoint?: string;
  // Called per request so an auth token is always read fresh (admin endpoint).
  headers?: () => Record<string, string> | undefined;
};

export function useQuote(input: QuoteInput, options: QuoteOptions = {}) {
  const debounceMs = options.debounceMs ?? 300;
  const endpoint = options.endpoint ?? '/api/quote';

  const quote = ref<QuoteResult | null>(null);
  const pending = ref(false);
  const error = ref<Error | null>(null);

  let timer: ReturnType<typeof setTimeout> | null = null;

  const fetchQuote = async () => {
    if (
      input.listingId.value == null ||
      !input.checkin.value ||
      !input.checkout.value ||
      !input.guests.value
    ) {
      quote.value = null;
      return;
    }
    pending.value = true;
    error.value = null;
    try {
      const res = await $fetch<QuoteResult>(endpoint, {
        method: 'POST',
        headers: options.headers?.(),
        body: {
          listing_id: input.listingId.value,
          checkin: input.checkin.value,
          checkout: input.checkout.value,
          guests: input.guests.value,
        },
      });
      quote.value = res;
    } catch (e) {
      error.value = e instanceof Error ? e : new Error(String(e));
      quote.value = null;
    } finally {
      pending.value = false;
    }
  };

  watch(
    [input.listingId, input.checkin, input.checkout, input.guests],
    () => {
      if (timer) clearTimeout(timer);
      // Clear the stale quote immediately so CTAs disable while the new one is in flight.
      quote.value = null;
      timer = setTimeout(fetchQuote, debounceMs);
    },
    { immediate: true },
  );

  onScopeDispose(() => {
    if (timer) clearTimeout(timer);
  });

  return { quote, pending, error, refresh: fetchQuote };
}
